import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import MobileAppLayout from "@/components/app/MobileAppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Mail, Phone, MessageCircle, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useAppMode } from "@/hooks/useAppMode";
import { supabase } from "@/integrations/supabase/client";

const WELLSUITES_PHONE = "+442080500395";
const WELLSUITES_PHONE_DISPLAY = "+44 20 8050 0395";

interface SupportFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
  priority: string;
}

const AppSupport = () => {
  const { user } = useAuth();
  const { provider } = useAppMode();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Load Crisp live chat (hidden until user taps "Start chat")
  useEffect(() => {
    if ((window as any).$crisp) return;
    (window as any).$crisp = [];
    (window as any).CRISP_WEBSITE_ID = "292c3d15-e80d-4229-9c3e-d92625d98217";
    (window as any).$crisp.push(["do", "chat:hide"]);
    (window as any).$crisp.push([
      "on",
      "chat:closed",
      () => (window as any).$crisp.push(["do", "chat:hide"]),
    ]);

    const script = document.createElement("script");
    script.src = "https://client.crisp.chat/l.js";
    script.async = true;
    document.head.appendChild(script);
  }, []);

  const openLiveChat = () => {
    const crisp = (window as any).$crisp;
    if (crisp) {
      crisp.push(["do", "chat:show"]);
      crisp.push(["do", "chat:open"]);
    }
  };

  const firstName = user?.user_metadata?.first_name || "";
  const lastName = user?.user_metadata?.last_name || "";
  const fullName = `${firstName} ${lastName}`.trim();

  const form = useForm<SupportFormData>({
    defaultValues: {
      name: fullName,
      email: user?.email || "",
      subject: "",
      message: "",
      priority: "medium",
    },
  });

  const onSubmit = async (data: SupportFormData) => {
    setIsSubmitting(true);
    setStatus(null);

    try {
      const { error } = await supabase.functions.invoke("send-mailersend-email", {
        body: {
          name: data.name,
          email: data.email,
          subject: `[APP SUPPORT${provider ? ` - ${provider.name}` : ""}] ${data.subject}`,
          message: `Provider: ${provider?.name || "N/A"}\n\n${data.message}`,
          priority: data.priority,
        },
      });

      if (error) throw error;

      setStatus({
        type: "success",
        text: "Message sent. We'll reply within 24 hours.",
      });
      form.reset({
        name: fullName,
        email: user?.email || "",
        subject: "",
        message: "",
        priority: "medium",
      });
    } catch (err) {
      console.error("Failed to send support request:", err);
      setStatus({
        type: "error",
        text: "Failed to send. Please try again or email us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const providerEmail = provider?.email || provider?.provider_contact_email;
  const providerPhone = provider?.phone;

  return (
    <MobileAppLayout title="Help & Support">
      <div className="px-4 py-5 space-y-5">
        {/* Quick contact */}
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2 px-1">
            Get in touch
          </h2>
          <Card>
            <CardContent className="p-0">
              {providerPhone && (
                <a
                  href={`tel:${providerPhone}`}
                  className="w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors text-left border-b border-border"
                >
                  <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                    <Phone className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">Call {provider?.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{providerPhone}</p>
                  </div>
                </a>
              )}
              {providerEmail && (
                <a
                  href={`mailto:${providerEmail}`}
                  className="w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors text-left border-b border-border"
                >
                  <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">Email {provider?.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{providerEmail}</p>
                  </div>
                </a>
              )}
              <button
                type="button"
                onClick={openLiveChat}
                className="w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors text-left border-b border-border"
              >
                <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                  <MessageCircle className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">Live chat</p>
                  <p className="text-xs text-muted-foreground truncate">
                    Chat with our team now
                  </p>
                </div>
              </button>
              <a
                href={`tel:${WELLSUITES_PHONE}`}
                className="w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors text-left border-b border-border"
              >
                <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                  <Phone className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">Call Support</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {WELLSUITES_PHONE_DISPLAY}
                  </p>
                </div>
              </a>
              <a
                href="mailto:support@wellsuites.co"
                className="w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors text-left"
              >
                <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                  <Mail className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">Email Support</p>
                  <p className="text-xs text-muted-foreground truncate">support@wellsuites.co</p>
                </div>
              </a>
            </CardContent>
          </Card>
        </div>

        {/* Contact form */}
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2 px-1">
            Send a message
          </h2>
          <Card>
            <CardContent className="p-4">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    rules={{ required: "Name is required" }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Your full name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    rules={{
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="you@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="subject"
                    rules={{ required: "Subject is required" }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Subject</FormLabel>
                        <FormControl>
                          <Input placeholder="What's this about?" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="priority"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Priority</FormLabel>
                        <FormControl>
                          <select
                            {...field}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="urgent">Urgent</option>
                          </select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="message"
                    rules={{ required: "Message is required" }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Message</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Describe your issue..."
                            className="min-h-[120px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {status && (
                    <div
                      className={`flex items-start gap-2 rounded-md p-3 text-sm ${
                        status.type === "success"
                          ? "bg-muted text-foreground"
                          : "bg-destructive/10 text-destructive"
                      }`}
                    >
                      {status.type === "success" ? (
                        <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                      ) : (
                        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                      )}
                      <span>{status.text}</span>
                    </div>
                  )}

                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send message"
                    )}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>

        {/* FAQ */}
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2 px-1">
            FAQ
          </h2>
          <Card>
            <CardContent className="p-4 space-y-4">
              <div>
                <p className="font-medium text-sm mb-1">How do I cancel a booking?</p>
                <p className="text-xs text-muted-foreground">
                  Open the booking from the Bookings tab and tap Cancel. Cancellation rules depend on the provider's policy.
                </p>
              </div>
              <div className="border-t border-border pt-4">
                <p className="font-medium text-sm mb-1">How do I reschedule?</p>
                <p className="text-xs text-muted-foreground">
                  Open the booking and tap Reschedule. Available within the provider's reschedule window.
                </p>
              </div>
              <div className="border-t border-border pt-4">
                <p className="font-medium text-sm mb-1">Where is my access code?</p>
                <p className="text-xs text-muted-foreground">
                  Access codes are generated 30 minutes before your session and shown on the booking details screen.
                </p>
              </div>
              <div className="border-t border-border pt-4">
                <p className="font-medium text-sm mb-1">How do I use a coupon?</p>
                <p className="text-xs text-muted-foreground">
                  Apply coupons during checkout. Manage saved coupons in Profile → Coupons.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </MobileAppLayout>
  );
};

export default AppSupport;
